import React from 'react';

const TEMP_ORDER = ['hot', 'warm', 'lukewarm', 'cold', 'dead'] as const;

const TEMP_COLORS: Record<string, { bar: string; dot: string; text: string; label: string }> = {
  hot:      { bar: 'bg-red-500',    dot: 'bg-red-400',    text: 'text-red-400',    label: 'Hot' },
  warm:     { bar: 'bg-orange-500', dot: 'bg-orange-400', text: 'text-orange-400', label: 'Warm' },
  lukewarm: { bar: 'bg-yellow-500', dot: 'bg-yellow-400', text: 'text-yellow-400', label: 'Lukewarm' },
  cold:     { bar: 'bg-blue-500',   dot: 'bg-blue-400',   text: 'text-blue-400',   label: 'Cold' },
  dead:     { bar: 'bg-slate-500',  dot: 'bg-slate-400',  text: 'text-slate-400',  label: 'Dead' },
};

interface TemperatureDistributionBarProps {
  breakdown?: Record<string, number> | null;
  className?: string;
}

const TemperatureDistributionBar: React.FC<TemperatureDistributionBarProps> = ({ breakdown, className = '' }) => {
  if (!breakdown) return null;

  const total = Object.values(breakdown).reduce((sum, v) => sum + v, 0);
  if (total === 0) return null;

  const segments = TEMP_ORDER
    .map((key) => ({ key, count: breakdown[key] ?? 0, config: TEMP_COLORS[key] }))
    .filter((s) => s.count > 0);

  return (
    <div className={className}>
      <div className="flex rounded-full h-3 overflow-hidden bg-slate-700/50">
        {segments.map((seg) => (
          <div
            key={seg.key}
            className={`${seg.config.bar} transition-all duration-500`}
            style={{ width: `${(seg.count / total) * 100}%` }}
            title={`${seg.config.label}: ${seg.count}`}
          />
        ))}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
        {segments.map((seg) => (
          <div key={seg.key} className="flex items-center gap-1.5">
            <span className={`w-2 h-2 rounded-full ${seg.config.dot}`} />
            <span className={`text-xs ${seg.config.text}`}>{seg.config.label}</span>
            <span className="text-xs text-slate-400 font-medium tabular-nums">{seg.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TemperatureDistributionBar;
